// Optional readouts: FPS / ping, speedometer and the net graph. Throttled; text is written only on change.

import { div, setClass, setHidden, span } from "./dom";
import { clamp01, pingTier, quantize } from "./format";
import type { NetGraphStats } from "./types";

const PERF_INTERVAL_MS = 250;
const SPEED_INTERVAL_MS = 50;
const NET_INTERVAL_MS = 250;
const HISTORY = 60;

export class PerfReadout {
  readonly root = div("hud-perf");
  private readonly fpsEl = span("hud-perf__item");
  private readonly pingEl = span("hud-perf__item");
  private readonly fpsNum = span("hud-perf__n");
  private readonly pingNum = span("hud-perf__n");
  private showFps = false;
  private showPing = true;
  private lastMs = -Infinity;
  private fps = -1;
  private ping = -1;
  private tier = -1;

  constructor() {
    this.fpsEl.append(this.fpsNum, span("hud-perf__u", "FPS"));
    this.pingEl.append(this.pingNum, span("hud-perf__u", "MS"));
    this.root.append(this.fpsEl, this.pingEl);
    this.fpsEl.hidden = true;
  }

  setVisible(showFps: boolean, showPing: boolean): void {
    this.showFps = showFps;
    this.showPing = showPing;
    setHidden(this.fpsEl, !showFps);
    setHidden(this.pingEl, !showPing);
    setHidden(this.root, !showFps && !showPing);
    this.lastMs = -Infinity; // refresh now
  }

  update(fps: number, pingMs: number, nowMs: number): void {
    if (!this.showFps && !this.showPing) return;
    if (nowMs - this.lastMs < PERF_INTERVAL_MS) return;
    this.lastMs = nowMs;
    const f = Math.round(fps);
    if (f !== this.fps) {
      this.fps = f;
      this.fpsNum.textContent = String(f);
    }
    const p = Math.round(pingMs);
    if (p !== this.ping) {
      this.ping = p;
      this.pingNum.textContent = String(p);
      const tier = pingTier(p);
      if (tier !== this.tier) {
        this.tier = tier;
        this.pingEl.dataset.tier = String(tier);
      }
    }
  }
}

export class Speedometer {
  readonly root = div("hud-speed");
  private readonly numEl = span("hud-speed__num");
  private readonly fill = span("hud-speed__fill");
  private on = false;
  private lastMs = -Infinity;
  private v = -1;
  private bar = -1;

  constructor() {
    const bar = div("hud-speed__bar");
    bar.append(this.fill);
    this.root.append(this.numEl, span("hud-speed__unit", "M/S"), bar);
    this.root.hidden = true;
  }

  setVisible(on: boolean): void {
    this.on = on;
    this.root.hidden = !on;
  }

  update(speed: number, nowMs: number): void {
    if (!this.on || nowMs - this.lastMs < SPEED_INTERVAL_MS) return;
    this.lastMs = nowMs;
    const v = quantize(Number.isFinite(speed) ? Math.max(0, speed) : 0, 0.1);
    if (v === this.v) return;
    this.v = v;
    this.numEl.textContent = v.toFixed(1);
    const bar = Math.round(clamp01(v / 16) * 100);
    if (bar !== this.bar) {
      this.bar = bar;
      this.fill.style.transform = `scaleX(${bar / 100})`;
    }
  }
}

const NET_ROWS = ["PING", "JITTER", "LOSS", "LEAD", "INTERP", "FIXES", "KBPS"] as const;
const GRAPH_W = 152;
const GRAPH_H = 36;

export class NetGraph {
  readonly root = div("hud-net");
  private readonly transportEl = div("hud-net__transport");
  private readonly canvas = document.createElement("canvas");
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly values: HTMLSpanElement[] = [];
  private readonly texts: string[] = [];
  private readonly history = new Float32Array(HISTORY);
  private histPos = 0;
  private histCount = 0;

  private stats: NetGraphStats | null = null;
  private enabled = false;
  private lastMs = -Infinity;
  private dpr = 1;

  constructor() {
    this.canvas.className = "hud-net__graph";
    this.canvas.style.width = `${GRAPH_W}px`;
    this.canvas.style.height = `${GRAPH_H}px`;
    this.ctx = this.canvas.getContext("2d");
    this.setDpr(1);
    const grid = div("hud-net__grid");
    for (const label of NET_ROWS) {
      const v = span("hud-net__val");
      this.values.push(v);
      this.texts.push("");
      grid.append(span("hud-net__key", label), v);
    }
    this.root.append(this.transportEl, this.canvas, grid);
    this.root.hidden = true;
  }

  setDpr(dpr: number): void {
    this.dpr = dpr;
    this.canvas.width = Math.round(GRAPH_W * dpr);
    this.canvas.height = Math.round(GRAPH_H * dpr);
  }

  /** `enabled` is `settings.hud.showNetGraph`; the graph shows when it is on and stats are present. */
  setStats(stats: NetGraphStats | null, enabled: boolean): void {
    const was = this.stats !== null && this.enabled;
    this.stats = stats;
    this.enabled = enabled;
    const now = stats !== null && enabled;
    this.root.hidden = !now;
    if (now && !was) {
      this.histCount = 0;
      this.histPos = 0;
      this.lastMs = -Infinity;
    }
  }

  update(nowMs: number): void {
    const s = this.stats;
    if (!s || !this.enabled || nowMs - this.lastMs < NET_INTERVAL_MS) return;
    this.lastMs = nowMs;
    if (this.transportEl.textContent !== s.transport) this.transportEl.textContent = s.transport;
    this.set(0, `${Math.round(s.pingMs)} ms`);
    this.set(1, `${s.jitterMs.toFixed(1)} ms`);
    this.set(2, `${s.lossPct.toFixed(1)} %`);
    this.set(3, `${s.inputLeadMs.toFixed(1)} ms`);
    this.set(4, `${Math.round(s.interpMs)} ms`);
    this.set(5, `${Math.round(s.correctionsPerMin)}/min \u00b7 ${s.lastCorrectionM.toFixed(2)} m`);
    this.set(6, `${Math.round(s.kbpsIn)} / ${Math.round(s.kbpsOut)}`);
    setClass(this.values[0]!, "is-bad", s.pingMs >= 120);
    setClass(this.values[2]!, "is-bad", s.lossPct >= 2);
    this.history[this.histPos] = s.pingMs;
    this.histPos = (this.histPos + 1) % HISTORY;
    if (this.histCount < HISTORY) this.histCount++;
    this.draw();
  }

  private set(i: number, text: string): void {
    if (this.texts[i] === text) return;
    this.texts[i] = text;
    this.values[i]!.textContent = text;
  }

  /** Ping history as bars: green below 60 ms, amber below 120 ms, red above. */
  private draw(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, GRAPH_W, GRAPH_H);
    let max = 100;
    for (let i = 0; i < this.histCount; i++) if (this.history[i]! > max) max = this.history[i]!;
    const bw = GRAPH_W / HISTORY;
    for (let i = 0; i < this.histCount; i++) {
      // oldest first
      const idx = (this.histPos - this.histCount + i + HISTORY * 2) % HISTORY;
      const ms = this.history[idx]!;
      const h = Math.max(1, (ms / max) * (GRAPH_H - 2));
      const tier = pingTier(ms);
      ctx.fillStyle = tier === 0 ? "#5be86f" : tier === 1 ? "#ffb23a" : "#ff6b4a";
      ctx.fillRect((HISTORY - this.histCount + i) * bw, GRAPH_H - h, Math.max(1, bw - 1), h);
    }
  }
}
